import { Component, OnInit, inject, OnDestroy, ChangeDetectionStrategy, ChangeDetectorRef, Input, Output, EventEmitter } from '@angular/core';
import { CommonModule } from '@angular/common';
import { ReactiveFormsModule, FormBuilder, FormGroup, FormArray, Validators, AbstractControl, FormControl, NonNullableFormBuilder } from '@angular/forms';
import { MatTableDataSource, MatTableModule } from '@angular/material/table';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatSelectModule } from '@angular/material/select';
import { MatInputModule } from '@angular/material/input';
import { MatButtonModule } from '@angular/material/button';
import { MatIconModule } from '@angular/material/icon';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { MatTooltipModule } from '@angular/material/tooltip';
import { MatDialog, MatDialogModule } from '@angular/material/dialog';

// --- RxJS ---
import { of, Subscription, Observable, throwError, firstValueFrom, filter, switchMap, tap, catchError, forkJoin, finalize, combineLatest, defaultIfEmpty, map } from 'rxjs';

// --- Models ---
import { ScheduleResponse, ScheduleOrderResponse, schedule } from '../../../models/schedule.model';
import { GetOrCreatePlaceRequest, PlaceResponse } from '../../../models/orders/place.model';
import { OrderStatusResponse } from '../../../models/orders/order.model';


// --- Custom Components ---
import { SearchPlaceDialogComponent } from '../../places/search-place-dialog/search-place-dialog.component';
import { QrScannerComponent } from '../../scanner-dialog/qr-scanner/qr-scanner.component';
import { DisplayPlaceFieldComponent } from '../../places/display-place-field/display-place-field.component';
import { SearchSingleOrderComponent } from '../../orders/search-single-order/search-single-order.component';
import { ScanPlaceCodeComponent } from '../../places/scan-place-code/scan-place-code.component';

// --- Services ---
import { OrderService } from '../../../services/order.service';
import { SchedulesService } from '../../../services/schedules.service';
import { ToastrService } from 'ngx-toastr';

// -- Pipes --
import { FilterStatusesPipe } from '../../../pipes/filter-statuses.pipe';
import { PlaceService } from '../../../services/place.service';
import { SelectionModel } from '@angular/cdk/collections';
import { MatCheckboxModule } from '@angular/material/checkbox';
import { SelectStatusDialogComponent } from '../../orders/order-statuses/select-status-dialog/select-status-dialog.component';

// --- Type Definition for the Form Group Row ---
interface ScheduleOrderFormRow {
  isPlaceholder: FormControl<boolean>;
  id: FormControl<string>; // scheduleOrderId or temp UUID
  scheduleId: FormControl<string>;
  orderId: FormControl<string>;
  orderRef: FormControl<number | null>;
  animalName: FormControl<string>;
  scheduleType: FormControl<string>;
  newSchedulingStatus: FormControl<string | null>; // Target ScheduleOrder status string
  placeId: FormControl<string | null>;
  newOrderStatus: FormControl<string>; // Target OrderStatus ID
  validStatusTransitions: FormControl<string[]>;
  isProcessing: FormControl<boolean>;
  isSuccess: FormControl<boolean>;
  errorMessage: FormControl<string | null>;
}
type ScheduleOrderFormGroup = FormGroup<ScheduleOrderFormRow>;

@Component({
  selector: 'app-bulk-process',
  standalone: true,
  imports: [
    CommonModule, 
    ReactiveFormsModule, 
    MatTableModule, 
    MatFormFieldModule, 
    MatSelectModule, 
    MatInputModule,
    MatButtonModule, 
    MatIconModule, 
    MatProgressSpinnerModule, 
    MatTooltipModule, 
    MatDialogModule,
    DisplayPlaceFieldComponent, 
    FilterStatusesPipe,
    MatCheckboxModule,
  ],
  templateUrl: './bulk-process.component.html',
  styleUrl: './bulk-process.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class BulkProcessComponent implements OnInit { // Removed OnDestroy
  // --- Injected Services ---
  private fb = inject(NonNullableFormBuilder);
  private cd = inject(ChangeDetectorRef);
  private dialog = inject(MatDialog);
  private orderService = inject(OrderService);
  private placeService = inject(PlaceService);
  private scheduleService = inject(SchedulesService);
  private toastr = inject(ToastrService);

  // --- Component Inputs ---
  @Input() currentSchedule: ScheduleResponse | null = null;
  @Input() canAddRows: boolean = true;
  @Input() orders: ScheduleOrderResponse[] = [];

  // --- Component Outputs ---
  @Output() submitted = new EventEmitter<void>();

  // --- Component State ---
  public dataSource = new MatTableDataSource<AbstractControl>([]);
  public bulkTransferForm: FormGroup<{ scheduleOrders: FormArray<ScheduleOrderFormGroup> }>;
  public isLoading = false;
  public isSubmitting = false;
  public allOrderStatuses: OrderStatusResponse[] = [];
  public selection = new SelectionModel<ScheduleOrderFormGroup>(true, []);

  // --- Configuration ---
  public readonly displayedColumns: string[] = [
    'select',
    'rowStatus', 
    'orderRef', 
    'animalName', 
    'newSchedulingStatus',
    'processingPlace', 
    'newOrderStatus', 
    'actions'
  ];
  public readonly validSchedulingStatuses: string[] = ["Scheduled", "InProgress", "Completed", "Cancelled"];

  // --- Lifecycle Hooks ---
  constructor() {
    this.bulkTransferForm = this.fb.group({
      scheduleOrders: this.fb.array<ScheduleOrderFormGroup>([]),
    });
  }

  async ngOnInit(): Promise<void> {
    this.isLoading = true;
    this.cd.markForCheck();

    try {
      const statusesLoaded = await this.fetchAllOrderStatuses();
      if (!statusesLoaded) {
        throw new Error("Initialization failed: Could not load necessary order statuses.");
      }

      if (this.currentSchedule) {
        this.initializeFormFromSchedule(this.currentSchedule);
        await this.fetchInitialValidTransitions();
      } else {
        console.warn("BulkProcessComponent initialized without a currentSchedule.");
        this.initializeForm([]);
      }
      this.loadScheduleStatuses();
    } catch (error) {
      console.error("Error during component initialization:", error);
      this.toastr.error("Failed to initialize component.", "Initialization Error");
    } finally {
      this.isLoading = false;
      this.cd.markForCheck();
    }
  }

  // --- Form Array Accessor ---
  public get scheduleOrdersFormArray(): FormArray<ScheduleOrderFormGroup> {
    return this.bulkTransferForm.get('scheduleOrders') as FormArray<ScheduleOrderFormGroup>;
  }

  // --- Data Loading & Setup ---

  private initializeFormFromSchedule(schedule: ScheduleResponse): void {
    let formGroups: ScheduleOrderFormGroup[] = [];
    //(schedule.orders || []).map(order => this.createScheduleOrderFormGroup(order));
    if(this.orders.length > 0) {
      formGroups = this.orders.map(order => this.createScheduleOrderFormGroup(order));
    }
    const formArray = this.fb.array(formGroups);
    this.bulkTransferForm.setControl('scheduleOrders', formArray);
    this.updateDataSource();
  }

  /** Fetches all possible order statuses once, assigns the result, and returns a promise indicating success. */
  private async fetchAllOrderStatuses(): Promise<boolean> {
    let success = false;
    try {
      const statusPagedResult = await firstValueFrom(this.orderService.getOrderStatuses());
      this.allOrderStatuses = statusPagedResult?.items || [];
      success = true;
    } catch (err) {
      console.error("Error fetching all order statuses from service:", err);
      this.toastr.error("Could not load the list of possible order statuses.", "Error");
      this.allOrderStatuses = [];
      success = false;
    }
    return success;
  }

  /** Fetch valid transitions for rows already present on the schedule */
  private async fetchInitialValidTransitions(): Promise<void> {
    const initialRows = this.scheduleOrdersFormArray.controls.filter(control =>
      !control.value.isPlaceholder && control.value.orderId
    );
    if (initialRows.length === 0) return;

    const fetchObservables = initialRows.map(rowGroup => {
      const orderId = rowGroup.value.orderId!;
      return this.orderService.getOrder(orderId).pipe(
        tap(orderResult => {
          const transitions = orderResult?.status?.validTransitions || [];
          rowGroup.controls.validStatusTransitions.setValue(transitions);
          if( transitions.length > 0) {
            rowGroup.controls.newOrderStatus.setValue(transitions[0] || '');
          }
        }),
        catchError(err => {
          console.error(`Error fetching initial details for Order ID ${orderId}:`, err);
          rowGroup.controls.validStatusTransitions.setValue([]);
          return of(null);
        })
      );
    });

    try {
      await firstValueFrom(combineLatest(fetchObservables).pipe(defaultIfEmpty([])));
    } catch (error) {
      console.error("Error occurred while waiting for initial transition fetches:", error);
    } finally {
      this.cd.markForCheck();
    }
  }

  private loadScheduleStatuses(): void {
    // Implement fetching of schedule-level statuses if they differ from order statuses
  }

  // Fallback initializer
  private initializeForm(orders: ScheduleOrderResponse[]): void {
    const formGroups = orders.map(order => this.createScheduleOrderFormGroup(order));
    this.bulkTransferForm.setControl('scheduleOrders', this.fb.array(formGroups));
    this.updateDataSource();
  }

  /** Creates a FormGroup for a single row in the table */
  private createScheduleOrderFormGroup(order: ScheduleOrderResponse | null): ScheduleOrderFormGroup {
    const isPlaceholder = !order;
    return this.fb.group<ScheduleOrderFormRow>({
      isPlaceholder: this.fb.control<boolean>(isPlaceholder),
      id: this.fb.control<string>(order?.id || crypto.randomUUID()),
      scheduleId: this.fb.control<string>(order?.scheduleId || this.currentSchedule?.id || ''),
      orderId: this.fb.control<string>(order?.orderId || ''),
      orderRef: this.fb.control<number | null>(order?.order?.referenceNumber || null),
      animalName: this.fb.control<string>(order?.order?.animalName || ''),
      scheduleType: this.fb.control<string>(order?.scheduleType || ''),
      newSchedulingStatus: this.fb.control<string | null>(this.getNextSchedulingStatus(order?.status ?? null), Validators.required),
      placeId: this.fb.control<string | null>(this.getNextPlaceId(order) || null, Validators.required),
      newOrderStatus: this.fb.control<string>(order?.order?.orderStatusId || '', Validators.required),
      validStatusTransitions: this.fb.control<string[]>([]),
      isProcessing: this.fb.control<boolean>(false),
      isSuccess: this.fb.control<boolean>(false),
      errorMessage: this.fb.control<string | null>(null)
    });
  }

  /** Attempt to fill in the next place ID based on some simple logic */
  public getNextPlaceId(order: ScheduleOrderResponse | null): string | null {
    if (!order) return null; // No order, no place ID
    if(order.scheduleType === 'Dropoff') {
      if(order.status === 'Scheduled') {
        // Order is going into vehicle
        if(!this.currentSchedule?.vehicle?.id || !this.currentSchedule?.vehicle?.name) {
          console.warn("Vehicle ID or name is missing in the current schedule.");
          return null; // No valid place ID
        }
        this.updatePlaceId(order.id, 'Vehicle', this.currentSchedule?.vehicle?.name, this.currentSchedule?.vehicle?.id);
        return null;
      }
      else if(order.status === 'InProgress') {
        this.updatePlaceId(order.id, 'Custom', 'Dropoff Address',undefined);
        return null;
      }
    }
    if(order.scheduleType === 'Pickup') {
      if(order.status === 'Scheduled') {
        if(!this.currentSchedule?.vehicle?.id || !this.currentSchedule?.vehicle?.name) {
          console.warn("Vehicle ID or name is missing in the current schedule.");
          return null; // No valid place ID
        }
        this.updatePlaceId(order.id, 'Vehicle', this.currentSchedule?.vehicle?.name, this.currentSchedule?.vehicle?.id)
        return null; // Use vehicle ID for pickup
      }
      else if(order.status === 'InProgress') {
        // Moving to some custom storage place
        this.updatePlaceId(order.id, 'Custom', 'General Freezer', undefined);
        return null; // Use custom place ID for pickup
      }
    }
    return null; // Default case
  }

  public updatePlaceId(orderId: string, type: string, name: string, objectId: string | undefined): void {
    let request:GetOrCreatePlaceRequest = {
      type: type,
      name: name,
      objectId: objectId
    };
    this.placeService.getOrCreatePlace(request).subscribe({
      next: (place) => {
        const rowGroup = this.scheduleOrdersFormArray.controls.find(c => c.value.id === orderId);
        if (!rowGroup) {
          console.warn(`Row group not found for order ID: ${orderId}`);
          return; // Row group not found, do nothing
        }
        if (rowGroup && place) {
          rowGroup.controls.placeId.setValue(place.id);
          rowGroup.controls.placeId.markAsTouched();
          this.cd.markForCheck();
        }
      },
      error: (err) => {
        console.error("Error fetching place ID:", err);
        this.toastr.error("Failed to fetch place ID.", "Error");
      }
    });
  }

  public getNextSchedulingStatus(currentStatus:string | null): string | null {
    if (!currentStatus) return null; // No current status, no next status
    const currentIndex = this.validSchedulingStatuses.indexOf(currentStatus);
    if (currentIndex === -1 || currentIndex === this.validSchedulingStatuses.length - 1) {
      console.warn("Could not find next scheduling status for:", currentStatus);
      return null; // No next status available
    }
    return this.validSchedulingStatuses[currentIndex + 1];
  }

  // --- Add / Remove Rows ---
  public addScheduleOrderRow(): void {
    const newRowFormGroup = this.createScheduleOrderFormGroup(null);
    this.scheduleOrdersFormArray.push(newRowFormGroup);
    this.updateDataSource();
    this.cd.markForCheck();
  }
  public removeScheduleOrderRow(index: number): void {
    if (index >= 0 && index < this.scheduleOrdersFormArray.length) {
      this.scheduleOrdersFormArray.removeAt(index);
      this.updateDataSource();
      this.cd.markForCheck();
    }
  }

  // --- Update Table Data Source ---
  private updateDataSource(): void {
    this.dataSource.data = this.scheduleOrdersFormArray.controls;
  }

  // --- Selection Helpers ---

  public isAllSelected(): boolean {
    const numSelected = this.selection.selected.length;
    const numRows = this.dataSource.data.length;
    return numSelected === numRows;
  }

// -- Bulk Actions --

  public openBulkPlaceDialog(): void {
    if (!this.selection.hasValue()) {
      this.toastr.info("No rows selected for bulk action.");
      return;
    }

    // Use the same search dialog as the single row action
    const dialogRef = this.dialog.open(SearchPlaceDialogComponent, {
      width: '600px',
      disableClose: true,
      autoFocus: false // Prevent focus trap issues sometimes
    });

    dialogRef.afterClosed()
      .pipe(filter((result): result is PlaceResponse => !!result)) // Ensure we have a PlaceResponse object
      .subscribe(selectedPlace => {
        let updatedCount = 0;
        this.selection.selected.forEach((rowGroup) => {
          // Apply only to rows that are identified and not already processed/processing
          if (!rowGroup.value.isPlaceholder && !rowGroup.value.isProcessing && !rowGroup.value.isSuccess) {
            rowGroup.controls.placeId.setValue(selectedPlace.id);
            rowGroup.controls.placeId.markAsTouched(); // Mark as touched for validation if needed
            updatedCount++;
          }
        });

        if (updatedCount > 0) {
          this.toastr.success(`Set place to "${selectedPlace.name}" for ${updatedCount} selected row(s).`);
          this.cd.markForCheck(); // Trigger change detection to update the view
        } else {
            this.toastr.info("No applicable rows found to update the place.", "Bulk Action");
        }
      });
  }

  public openBulkStatusDialog(): void {
    if (!this.selection.hasValue()) {
      this.toastr.info("No rows selected for bulk action.");
      return;
    }

    const validBulkStatuses = this.getValidBulkTransitionStatuses();

    if (validBulkStatuses.length === 0) {
      this.toastr.warning("No common target status available for all selected rows.", "Bulk Status Unavailable");
      return;
    }

    const dialogRef = this.dialog.open(SelectStatusDialogComponent, {
      width: '400px',
      data: { statuses: validBulkStatuses },
      disableClose: true
    });

    dialogRef.afterClosed()
      .pipe(filter((result): result is OrderStatusResponse => !!result)) // Ensure we have an OrderStatusResponse
      .subscribe(selectedStatus => {
        let updatedCount = 0;
        this.selection.selected.forEach((rowGroup) => {
          // Apply only to rows that are identified and not already processed/processing
          if (!rowGroup.value.isPlaceholder && !rowGroup.value.isProcessing && !rowGroup.value.isSuccess) {
            // Double-check if this status is indeed valid for this specific row (should be, due to intersection logic)
            if ((rowGroup.value.validStatusTransitions || []).includes(selectedStatus.id)) {
                 rowGroup.controls.newOrderStatus.setValue(selectedStatus.id);
                 rowGroup.controls.newOrderStatus.markAsTouched();
                 updatedCount++;
             } else {
                 // This case should ideally not happen if getValidBulkTransitionStatuses logic is correct
                 console.warn(`Row ${rowGroup.value.orderRef} was selected but cannot transition to bulk status ${selectedStatus.name}. Skipping.`);
             }
          }
        });

        if (updatedCount > 0) {
          this.toastr.success(`Set target status to "${selectedStatus.name}" for ${updatedCount} selected row(s).`);
          this.cd.markForCheck(); // Trigger change detection
        } else {
             this.toastr.info("No applicable rows found to update the status.", "Bulk Action");
        }
      });
  }


  // -- Bulk Actions Helpers --

  private getValidBulkTransitionStatuses(): OrderStatusResponse[] {
    if (!this.selection.hasValue()) {
      return [];
    }

    const applicableRows = this.selection.selected.filter(row =>
      !row.value.isPlaceholder && !row.value.isProcessing && !row.value.isSuccess
    );

    if (applicableRows.length === 0) {
      return []; // No valid rows selected to determine common statuses
    }

    // Start with the valid transitions of the first applicable row
    let commonTransitionIds = new Set<string>(applicableRows[0].value.validStatusTransitions || []);

    // Find intersection with subsequent rows
    for (let i = 1; i < applicableRows.length; i++) {
      const currentRowTransitions = new Set<string>(applicableRows[i].value.validStatusTransitions || []);
      commonTransitionIds.forEach(id => {
        if (!currentRowTransitions.has(id)) {
          commonTransitionIds.delete(id); // Remove if not present in the current row's transitions
        }
      });
      // Optimization: if intersection becomes empty, no need to check further
      if (commonTransitionIds.size === 0) break;
    }

    // Convert the final set of IDs back to OrderStatusResponse objects
    return this.allOrderStatuses.filter(status => commonTransitionIds.has(status.id));
  }

  public toggleAllRows(): void {
    this.isAllSelected() ? this.selection.clear() : this.dataSource.data.forEach(row => this.selection.select(row as ScheduleOrderFormGroup));
  }


  // --- === Order Identification Logic === ---

  /** Opens the Order Search Dialog for a specific placeholder row */
  public openOrderSearchDialog(index: number): void {
    const rowGroup = this.scheduleOrdersFormArray.at(index);
    if (!rowGroup || !rowGroup.value.isPlaceholder || rowGroup.value.isProcessing || this.isSubmitting) return;

    const dialogRef = this.dialog.open(SearchSingleOrderComponent, { width: '400px', disableClose: true, autoFocus: true });
    dialogRef.afterClosed().subscribe(result => {
      if (result !== null && result !== undefined && result !== '') {
        const orderNumber = Number(result);
        if (!isNaN(orderNumber)) {
          this.identifyOrder(orderNumber, index);
        } else {
          this.toastr.error(`Invalid order number format: '${result}'.`, "Invalid Input");
        }
      }
    });
  }

  /** Opens the QR Scanner Dialog for identifying an order for a specific placeholder row */
  public openOrderScanDialog(index: number): void {
    const rowGroup = this.scheduleOrdersFormArray.at(index);
    if (!rowGroup || !rowGroup.value.isPlaceholder || rowGroup.value.isProcessing || this.isSubmitting) return;

    const dialogRef = this.dialog.open(QrScannerComponent, { width: '500px', disableClose: true });
    dialogRef.afterClosed().subscribe(result => {
      if (result && typeof result === 'string') {
        this.identifyOrder(result, index);
      } else if (result) {
        this.toastr.warning("Invalid data received from scanner.", "Scan Warning");
      }
    });
  }

  /** Attempts to find the ScheduleOrder on schedule, fetches Order details, populates the row */
  private async identifyOrder(identifier: string | number, index: number): Promise<void> {
    const rowGroup = this.scheduleOrdersFormArray.at(index);
    if (!rowGroup || !this.currentSchedule) { this.toastr.error("Missing schedule context.", "Error"); return; }

    rowGroup.controls.isProcessing.setValue(true); rowGroup.controls.errorMessage.setValue(null); this.cd.markForCheck();

    // 1. Find on schedule
    let foundScheduleOrder: ScheduleOrderResponse | undefined;
    if (typeof identifier === 'number') { foundScheduleOrder = this.currentSchedule.orders.find(so => so.order?.referenceNumber === identifier); }
    else if (typeof identifier === 'string') { foundScheduleOrder = this.currentSchedule.orders.find(so => so.orderId === identifier || so.id === identifier); }
    if (!foundScheduleOrder || !foundScheduleOrder.orderId) { this.handleIdentificationError(rowGroup, `Order '${identifier}' not found on schedule.`); this.cd.markForCheck(); return; }

    // 2. Check duplicates
    const orderIdToFind = foundScheduleOrder.orderId;
    const existingIdentifiedIndex = this.scheduleOrdersFormArray.controls.findIndex((ctrl, idx) => idx !== index && !ctrl.value.isPlaceholder && ctrl.value.orderId === orderIdToFind );
    if (existingIdentifiedIndex !== -1) { this.handleIdentificationError(rowGroup, `Order #${foundScheduleOrder.order?.referenceNumber ?? orderIdToFind} already identified.`); this.cd.markForCheck(); return; }

    // 3. Fetch full Order details
    try {
      const orderResult = await firstValueFrom(this.orderService.getOrder(orderIdToFind));
      if (!orderResult) { throw new Error(`Could not retrieve details for Order ID: ${orderIdToFind}.`); }

      rowGroup.patchValue({
        isPlaceholder: false, 
        id: foundScheduleOrder.id, 
        scheduleId: foundScheduleOrder.scheduleId, 
        orderId: orderResult.id,
        orderRef: orderResult.referenceNumber, 
        animalName: orderResult.animalName, 
        scheduleType: foundScheduleOrder.scheduleType,
        newSchedulingStatus: this.getNextSchedulingStatus(foundScheduleOrder.status) || null,
        placeId: orderResult.currentPlace?.id || null,
        newOrderStatus: orderResult.status?.validTransitions[0] || '',
        validStatusTransitions: orderResult.status?.validTransitions || [],
        isProcessing: false, 
        isSuccess: false, 
        errorMessage: null
      });
      this.toastr.success(`Order #${orderResult.referenceNumber} identified.`);
    } catch (err: any) {
      this.handleIdentificationError(rowGroup, err?.message || `Failed to fetch order details.`);
    } finally {
      // Ensure processing flag is always turned off for the row
      if (rowGroup.value.isProcessing) { rowGroup.controls.isProcessing.setValue(false); }
      this.cd.markForCheck();
    }
  }

  /** Helper to handle errors during order identification */
  private handleIdentificationError(rowGroup: ScheduleOrderFormGroup, errorMessage: string): void {
    rowGroup.controls.isProcessing.setValue(false);
    rowGroup.controls.errorMessage.setValue(errorMessage);
    this.toastr.error(errorMessage, 'Order Identification Failed');
    // Caller should handle markForCheck
  }
  // --- === End: Order Identification Logic === ---


  // --- Place Handling Logic ---
  public openPlaceSearchDialog(index: number): void {
    const rowGroup = this.scheduleOrdersFormArray.at(index);
    if (!rowGroup || rowGroup.value.isPlaceholder || this.isSubmitting || rowGroup.value.isProcessing) return;
    const dialogRef = this.dialog.open(SearchPlaceDialogComponent, { width: '600px', disableClose: true, autoFocus: false });
    dialogRef.afterClosed().pipe(filter((r): r is PlaceResponse => !!r)).subscribe(p => {
        rowGroup.controls.placeId.setValue(p.id); rowGroup.controls.placeId.markAsTouched(); this.cd.markForCheck();
    });
  }

  public openPlaceScanDialog(index: number): void {
    const rowGroup = this.scheduleOrdersFormArray.at(index);
    if (!rowGroup || rowGroup.value.isPlaceholder || this.isSubmitting || rowGroup.value.isProcessing) return;
    const dialogRef = this.dialog.open(ScanPlaceCodeComponent, { width: '500px' });
    dialogRef.afterClosed().pipe(
        filter((r): r is PlaceResponse => !!r && typeof r === 'object' && 'id' in r)
    ).subscribe({
        next: (p) => { rowGroup.controls.placeId.setValue(p.id); rowGroup.controls.placeId.markAsTouched(); this.cd.markForCheck(); },
        error: (err) => { console.error("Error after place scan dialog:", err); this.toastr.error("Unexpected error after scanning.", "Error");}
    });
  }

  public clearPlace(index: number): void {
    const rowGroup = this.scheduleOrdersFormArray.at(index);
    if (!rowGroup || rowGroup.value.isPlaceholder || this.isSubmitting || rowGroup.value.isProcessing) return;
    rowGroup.controls.placeId.setValue(null);
    rowGroup.controls.placeId.markAsUntouched();
    this.cd.markForCheck();
  }
  // --- End Place Handling ---


  // --- Form Submission ---
  public async onSubmit(): Promise<void> {
    if (!this.currentSchedule?.id) { this.toastr.error("Missing schedule context.", "Error"); return; }
    // Use getRawValue() to include disabled control values, then check validity
    const formValue = this.bulkTransferForm.getRawValue();
    if (!this.bulkTransferForm.valid || this.isSubmitting) {
        if (this.bulkTransferForm.invalid) {
            this.toastr.error('Please correct invalid fields.', 'Invalid Form');
            this.bulkTransferForm.markAllAsTouched(); // Ensure errors are shown
        }
        return;
    }

    // Use the raw value for processing, filtering out placeholders
    const controlsToProcessData = formValue.scheduleOrders.filter(row => !row.isPlaceholder);
    const controlsInFormArray = this.scheduleOrdersFormArray.controls.filter(c => !c.value.isPlaceholder); // Get actual controls for patching

    if (controlsToProcessData.length === 0) { this.toastr.info('No identified orders to process.', 'No Orders'); return; }

    this.isSubmitting = true; this.cd.markForCheck();
    const promises: Promise<ScheduleOrderResponse | null>[] = [];
    console.log(`Starting submission for ${controlsToProcessData.length} rows.`);

    for (let i = 0; i < controlsToProcessData.length; i++) {
        const rowData = controlsToProcessData[i];
        const rowGroup = controlsInFormArray[i] as ScheduleOrderFormGroup; // Get corresponding FormGroup

        // Skip rows already successfully processed in this session (check FormGroup state)
        if (rowGroup.value.isSuccess) { continue; }
        // Validity check already done for the whole form

        // --- Gather Data for API Call from rowData (raw value) ---
        const scheduleOrderId = rowData.id!;
        const orderId = rowData.orderId!;
        const targetOrderStatusId = rowData.newOrderStatus!; // Target OrderStatus ID
        const placeId = rowData.placeId!;
        const scheduleId = this.currentSchedule.id;
        const scheduleOrderStatus = rowData.newSchedulingStatus!; // Target ScheduleOrder Status

        // --- Update Row State & Disable Controls on the FormGroup ---
        rowGroup.controls.isProcessing.setValue(true); rowGroup.controls.isSuccess.setValue(false); rowGroup.controls.errorMessage.setValue(null);
        rowGroup.controls.newOrderStatus.disable(); rowGroup.controls.newSchedulingStatus.disable(); rowGroup.controls.placeId.disable();
        this.cd.markForCheck(); // Show processing state for this specific row

        // --- Create Promise for the API call ---
        const promise = firstValueFrom(
            this.scheduleService.processScheduleOrder(
                scheduleId, orderId, targetOrderStatusId, placeId, scheduleOrderStatus
            ).pipe(
                tap((updatedScheduleOrder: ScheduleOrderResponse) => { // Success
                    rowGroup.patchValue({ // Patch the FormGroup
                        isProcessing: false, isSuccess: true,
                        newSchedulingStatus: updatedScheduleOrder.status, // Update actual status from response
                        errorMessage: null
                    });
                    // Controls remain disabled after success
                }),
                catchError((error) => { // Error
                    const message = error?.error?.message || error?.message || 'Update failed';
                    rowGroup.patchValue({ isProcessing: false, isSuccess: false, errorMessage: message });
                    // Re-enable controls on the FormGroup
                    rowGroup.controls.newOrderStatus.enable(); rowGroup.controls.newSchedulingStatus.enable(); rowGroup.controls.placeId.enable();
                    return of(null);
                }),
                finalize(() => this.cd.markForCheck()) // Check row after completion/error
            ) // End pipe
        ); // End firstValueFrom
        promises.push(promise);
    } // --- End for loop ---

    // --- Wait for all API calls ---
    try {
        await Promise.all(promises);
        const successCount = controlsToProcessData.filter((_, i) => controlsInFormArray[i].value.isSuccess).length;
        const errorCount = controlsToProcessData.length - successCount;
        this.submitted.emit(); // Emit event to parent component
        if (errorCount > 0) { this.toastr.warning(`Processed ${successCount} orders, ${errorCount} failed.`, 'Processing Finished'); }
        else { this.toastr.success(`Successfully processed all ${successCount} orders.`, 'Processing Complete'); }
    } catch (error) {
        console.error("Unexpected error awaiting updates:", error); this.toastr.error('Unexpected error during submission.', 'Error');
    } finally {
        this.isSubmitting = false;
        this.cd.markForCheck(); // Ensure final UI state
    }
  } // End onSubmit


  // --- Template Helper ---
  public getFormControl(control: AbstractControl | null, controlName: keyof ScheduleOrderFormRow): FormControl | null {
    if (control instanceof FormGroup) {
      return control.get(controlName) as FormControl | null;
    }
    return null;
  }
} // End Component Class
